import { Button, Dropdown, Input, Space, Spin, App } from '@myua/kit';
import dayjs from 'dayjs';
import { More } from '@myua/icons/dist/linear14';
import '../styles.scss';
import * as commentActions from '../actions';
import { useDispatch, useSelector } from 'react-redux';
import {
  getEditingComment,
  getInProgress,
  getNewComment,
  getComments,
  getCurrentAssetId,
} from '../selectors';
import { getUser } from '../../app/selectors';
import { FC } from 'react';

const MAX_COMMENT_LENGTH = 3000;

export const Comments: FC<{ outerSpinner?: boolean }> = ({ outerSpinner }) => {
  const dispatch = useDispatch();
  const assetId = useSelector(getCurrentAssetId);
  const allComments = useSelector(getComments);
  const comments = assetId === undefined ? undefined : allComments[assetId];
  const newComment = useSelector(getNewComment);
  const inProgress = useSelector(getInProgress);
  const user = useSelector(getUser);
  const editingComment = useSelector(getEditingComment);
  const { modal } = App.useApp();

  if (assetId === undefined) {
    return null;
  }

  return (
    <Spin spinning={!outerSpinner && Boolean(inProgress[assetId])}>
      <div className="rep-comments">
        <div className="rep-comments__list">
          {comments?.map((comment) => (
            <div key={comment.id} className="rep-comment">
              <div className="rep-comment__header">
                <div className="rep-comment__author">{comment.author}</div>
                <div className="rep-comment__date">
                  {dayjs(comment.updated_at ?? comment.created_at).format('DD.MM.YYYY HH:mm')}
                </div>
                <div className="rep-comment__space" />
                {comment.author_id === user?.id ? (
                  <Dropdown
                    trigger={['click']}
                    placement="bottomRight"
                    getPopupContainer={() =>
                      document.querySelector<HTMLElement>('.al-finder-details__info') ??
                      document.body
                    }
                    menu={{
                      items: [
                        {
                          label: 'Edit',
                          key: 'edit',
                          onClick: () =>
                            dispatch(
                              commentActions.setEditingComment({
                                id: comment.id,
                                content: comment.content,
                              }),
                            ),
                        },
                        {
                          label: 'Delete',
                          key: 'delete',
                          onClick: () => {
                            modal.confirm({
                              title: 'Are you sure you want to delete this comment?',
                              style: { position: 'fixed', top: 'unset', bottom: 200, right: 100 },
                              okText: 'Delete',
                              onOk: (close) => {
                                dispatch(
                                  commentActions.deleteClicked({
                                    assetId,
                                    commentId: comment.id,
                                  }),
                                );
                                close();
                              },
                            });
                          },
                        },
                      ],
                    }}
                  >
                    <Button size="small" type="text">
                      <More />
                    </Button>
                  </Dropdown>
                ) : null}
              </div>
              <div className="rep-comment__body">
                {editingComment?.id === comment.id ? (
                  <div>
                    <Input.TextArea
                      rows={3}
                      autoFocus
                      value={editingComment.content}
                      maxLength={MAX_COMMENT_LENGTH}
                      onChange={(e) =>
                        dispatch(
                          commentActions.setEditingComment({
                            ...editingComment,
                            content: e.target.value,
                          }),
                        )
                      }
                    />
                    <div className="rep-comment-footer">
                      <div className="rep-comment-footer__length">
                        {editingComment.content.trim().length} / {MAX_COMMENT_LENGTH}
                      </div>
                      <Space size="small">
                        <Button
                          size="small"
                          onClick={() => dispatch(commentActions.setEditingComment(undefined))}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          type="primary"
                          disabled={editingComment.content.trim().length === 0}
                          onClick={() =>
                            dispatch(
                              commentActions.changeSubmitted({
                                assetId,
                                commentId: editingComment.id,
                                content: editingComment.content.trim(),
                              }),
                            )
                          }
                        >
                          Save
                        </Button>
                      </Space>
                    </div>
                  </div>
                ) : (
                  <div className="rep-comment__content">{comment.content}</div>
                )}
              </div>
            </div>
          ))}
        </div>

        {editingComment || !user ? null : (
          <div className="rep-comment-new">
            <Input.TextArea
              rows={3}
              value={newComment.content}
              onChange={(e) =>
                dispatch(commentActions.setNewComment({ assetId, content: e.target.value }))
              }
              maxLength={MAX_COMMENT_LENGTH}
            />
            <div className="rep-comment-footer">
              <div className="rep-comment-footer__length">
                {newComment.content.trim().length} / {MAX_COMMENT_LENGTH}
              </div>
              <Button
                size="small"
                type="primary"
                onClick={() => {
                  const content = newComment.content.trim();
                  if (content.length === 0) {
                    return;
                  }
                  dispatch(commentActions.addClicked({ assetId, content }));
                }}
              >
                Add
              </Button>
            </div>
          </div>
        )}
      </div>
    </Spin>
  );
};
